import { WarningAmberOutlined } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";

import { useLiveMapGridRowSelection, usePageContext } from "@ryde/redux";
import {
  ComputedDataKey,
  DragTypeEnum,
  IRideTableDto,
  IRydeRow,
  isStarredTableRowIdSF,
  RideComputedDataKeys,
  ridesDraggingDataAtom,
  stopEventPropagation,
  useHasPermissions,
  useIsMobile,
  useSystemParams,
} from "@ryde/shared";
import { orangeMui, purpleMui, rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../../../common";
import { CheckboxMui, CheckboxStar } from "../../../../controls";
import { MultiIconCellRideMapStyled } from "../../../generic/cells/cell.multi-icon.styled";
import { LiveMapColorIndicator } from "./live-map-color-indicator";

const MultiIconLiveMapRowStyled = styled(MultiIconCellRideMapStyled)`
  margin-inline-start: 0;
  flex-wrap: nowrap;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  /* Use flex gap only — base multi-icon margins squeeze the live-map cluster */
  .color-circle-single-select {
    margin-inline-start: 0;
    margin-inline-end: 0;
  }
  /* FramerTooltip wrappers: give checkbox/star room like main rides grid */
  > span[style*="display: inline;"] {
    min-width: 21px;
    display: inline-flex;
    justify-content: center;
  }
  .live-map-row-star {
    margin-inline-start: 2px;
  }
  .live-map-row-new-incident-icon {
    font-size: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${orangeMui._800};
  }
`;

interface ICellMultiIconRideMap {
  row: IRydeRow<IRideTableDto>;
}

export const CellMultiIconRideMap: FC<ICellMultiIconRideMap> = ({ row }) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const rideId = row.original.id;
  const { type: dragType } = useRecoilValue(ridesDraggingDataAtom);
  const { hasPermissions: canReadIncidents } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { enableIncidents } = useSystemParams();
  const showIncidentWarningIcon = Boolean(enableIncidents) && canReadIncidents;
  const newIncidentsCount =
    row.original[ComputedDataKey]?.[RideComputedDataKeys.newIncidentsCount];
  const hasNewIncidents =
    typeof newIncidentsCount === "number" && newIncidentsCount > 0;

  const { isSelected, toggleRowSelection } = useLiveMapGridRowSelection(rideId);
  const [isStarred, setIsStarred] = useRecoilState(
    isStarredTableRowIdSF([entity, rideId]),
  );

  const handleSelectToggle = useEvent((): void => {
    toggleRowSelection();
  });

  const handleStarToggle = useEvent((): void => {
    setIsStarred(starred => !starred);
  });

  const isTooltipDisabled = useMemo(() => {
    return Boolean(dragType && dragType !== DragTypeEnum.NOT_DRAGGING);
  }, [dragType]);

  return (
    <MultiIconLiveMapRowStyled onClick={stopEventPropagation}>
      <LiveMapColorIndicator
        rideId={rideId}
        courseLineColor={
          row.original[ComputedDataKey]?.[RideComputedDataKeys.courseLineColor]
        }
        isSelected={isSelected}
        onToggle={handleSelectToggle}
        tooltipDisabled={isTooltipDisabled}
        tooltipContent={t("ride.RIDE_SELECT_TOOLTIP")}
      />
      <span onClick={stopEventPropagation} role="presentation">
        <CheckboxMui
          checked={isSelected}
          onCheckboxToggle={handleSelectToggle}
        />
      </span>
      {!isMobile && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("ride.RIDE_STAR_TOOLTIP")}
        >
          <span
            className="live-map-row-star"
            onClick={stopEventPropagation}
            role="presentation"
          >
            <CheckboxStar
              checked={isStarred}
              strokeColor={
                row.original?.courseId ? purpleMui._300 : rydeColors.grey
              }
              onCheckboxToggle={handleStarToggle}
            />
          </span>
        </FramerTooltip>
      )}
      {!isMobile && showIncidentWarningIcon && hasNewIncidents && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("rideMap.ROW_NEW_INCIDENTS_TOOLTIP")}
        >
          <span onClick={stopEventPropagation} role="presentation">
            <WarningAmberOutlined className="live-map-row-new-incident-icon" />
          </span>
        </FramerTooltip>
      )}
    </MultiIconLiveMapRowStyled>
  );
};
